import {
  Sparkles, Copy, Lightbulb, FileText, TrendingUp, ThumbsUp, ThumbsDown, Zap, CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";

const INTENT_STYLE = {
  informational: "bg-blue-100 text-blue-700",
  commercial: "bg-amber-100 text-amber-700",
  transactional: "bg-emerald-100 text-emerald-700",
};
const OPP_STYLE = {
  high: "bg-emerald-100 text-emerald-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-slate-100 text-slate-600",
};
const PLATFORM_STYLE = {
  Facebook: "bg-[#1877F2]/10 text-[#1877F2]",
  Instagram: "bg-pink-100 text-pink-600",
  X: "bg-brand-navy/10 text-brand-navy",
  LinkedIn: "bg-[#0A66C2]/10 text-[#0A66C2]",
};

const Badge = ({ className, children }) => (
  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold capitalize ${className || "bg-slate-100 text-slate-600"}`}>{children}</span>
);

const copy = (text) => {
  navigator.clipboard?.writeText(text).then(
    () => toast.success("Copied to clipboard"),
    () => toast.error("Could not copy"),
  );
};

function SeoView({ d }) {
  return (
    <div className="space-y-6" data-testid="marketing-seo-content">
      <div>
        <p className="font-bold text-brand-navy flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-brand-red" /> Target keywords</p>
        <div className="rounded-xl border border-brand-line overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-bg text-brand-navy">
                <th className="text-left font-bold px-3 py-2">Keyword</th>
                <th className="text-left font-bold px-3 py-2">Intent</th>
                <th className="text-left font-bold px-3 py-2">Opportunity</th>
                <th className="text-left font-bold px-3 py-2">Why</th>
              </tr>
            </thead>
            <tbody>
              {(d.keywords || []).map((k, i) => (
                <tr key={i} className="border-t border-brand-line align-top">
                  <td className="px-3 py-2.5 font-semibold text-brand-navy">{k.term}</td>
                  <td className="px-3 py-2.5"><Badge className={INTENT_STYLE[k.intent]}>{k.intent}</Badge></td>
                  <td className="px-3 py-2.5"><Badge className={OPP_STYLE[k.opportunity]}>{k.opportunity}</Badge></td>
                  <td className="px-3 py-2.5 text-brand-ink2">{k.why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <p className="font-bold text-brand-navy flex items-center gap-2 mb-3"><Lightbulb className="w-4 h-4 text-brand-red" /> Content ideas</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {(d.content_ideas || []).map((c, i) => (
            <div key={i} className="rounded-xl border border-brand-line p-3.5 bg-brand-bg">
              <p className="font-bold text-brand-navy">{c.title}</p>
              <p className="text-xs text-brand-red font-semibold mt-1">{c.target_keyword}</p>
              <p className="text-sm text-brand-ink2 mt-1.5">{c.angle}</p>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="font-bold text-brand-navy flex items-center gap-2 mb-3"><FileText className="w-4 h-4 text-brand-red" /> Meta tag suggestions</p>
        <div className="space-y-2.5">
          {(d.meta_suggestions || []).map((m, i) => (
            <div key={i} className="rounded-xl border border-brand-line p-3.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold uppercase tracking-wide text-brand-navy">{m.page}</span>
                <button onClick={() => copy(`${m.title}\n${m.description}`)} className="btn-focus text-brand-ink2 hover:text-brand-red" title="Copy"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="font-semibold text-brand-navy mt-1">{m.title}</p>
              <p className="text-sm text-brand-ink2">{m.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SocialView({ d, onSchedule }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="marketing-social-content">
      {(d.posts || []).map((p, i) => (
        <div key={i} className="rounded-xl border border-brand-line p-4 flex flex-col">
          <div className="flex items-center justify-between gap-2 mb-2">
            <Badge className={PLATFORM_STYLE[p.platform]}>{p.platform}</Badge>
            <button onClick={() => copy(`${p.caption}\n\n${(p.hashtags || []).join(" ")}`)} className="btn-focus text-brand-ink2 hover:text-brand-red" title="Copy post"><Copy className="w-4 h-4" /></button>
          </div>
          {p.theme && <p className="text-xs font-semibold text-brand-ink2 mb-1.5">{p.theme}</p>}
          <p className="text-brand-navy text-sm whitespace-pre-wrap leading-relaxed flex-1">{p.caption}</p>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(p.hashtags || []).map((h, j) => (
              <span key={j} className="text-xs font-semibold text-brand-red bg-brand-red/8 px-2 py-0.5 rounded-full">{h}</span>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 mt-3">
            {p.cta ? <p className="text-sm font-bold text-brand-navy flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-brand-red" /> {p.cta}</p> : <span />}
            {onSchedule && (
              <button
                onClick={() => onSchedule(p)}
                data-testid={`social-schedule-${i}`}
                className="btn-focus inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-brand-line text-sm font-semibold text-brand-navy hover:border-brand-navy hover:bg-brand-bg transition-colors whitespace-nowrap"
              >
                <CalendarPlus className="w-3.5 h-3.5 text-brand-red" /> Schedule
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CompetitorsView({ d }) {
  return (
    <div className="space-y-5" data-testid="marketing-competitors-content">
      <div className="grid sm:grid-cols-2 gap-4">
        {(d.competitors || []).map((c, i) => (
          <div key={i} className="rounded-xl border border-brand-line p-4">
            <p className="font-display font-bold text-brand-navy">{c.name}</p>
            <p className="text-sm text-brand-ink2 mt-1">{c.positioning}</p>
            <div className="mt-3 space-y-2 text-sm">
              <div>
                <p className="font-semibold text-emerald-700 flex items-center gap-1.5"><ThumbsUp className="w-3.5 h-3.5" /> Strengths</p>
                <ul className="list-disc pl-5 text-brand-ink2">{(c.strengths || []).map((s, j) => <li key={j}>{s}</li>)}</ul>
              </div>
              <div>
                <p className="font-semibold text-brand-red flex items-center gap-1.5"><ThumbsDown className="w-3.5 h-3.5" /> Weaknesses</p>
                <ul className="list-disc pl-5 text-brand-ink2">{(c.weaknesses || []).map((s, j) => <li key={j}>{s}</li>)}</ul>
              </div>
            </div>
            <div className="mt-3 rounded-lg bg-brand-bg border border-brand-line p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-brand-red">Our edge</p>
              <p className="text-sm text-brand-navy mt-0.5">{c.our_edge}</p>
            </div>
          </div>
        ))}
      </div>
      {(d.takeaways || []).length > 0 && (
        <div className="rounded-xl bg-brand-navy p-4">
          <p className="font-bold text-white flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-brand-red" /> Strategic takeaways</p>
          <ul className="space-y-1.5">
            {d.takeaways.map((t, i) => (
              <li key={i} className="text-white/90 text-sm flex gap-2"><span className="text-brand-red font-bold">›</span>{t}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export const VIEWS = { seo: SeoView, social: SocialView, competitors: CompetitorsView };
